import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useWatch } from "react-hook-form";

import { find, times } from "lodash";

import useTranslations from "../../../hooks/useTranslations";
import useSchedule from "../../../hooks/useSchedule";
import useDateFnsLocale from "../../../hooks/useDateFnsLocale";

import { FormStepProps } from "../../../screens/MadFormScreen";

import { DatePicker, Select, Input, FormLabel } from "../../wombat-ui";
import { MadFullFragment } from "../../../graphql/fragments";

const DAYS_IN_MONTH = [
  "31",
  "29",
  "31",
  "30",
  "31",
  "30",
  "31",
  "31",
  "30",
  "31",
  "30",
  "31",
];

const MadSchedulingForm: React.FC<
  Omit<FormStepProps, "onNext" | "formState"> & {
    defaultValues?: MadFullFragment;
  }
> = ({ register, unregister, setValue, control, defaultValues }) => {
  const t = useTranslations();

  const { startDate, endDate } = useWatch({
    control,
    name: ["startDate", "endDate"],
  });

  useEffect(() => {
    if (endDate && startDate && startDate > endDate)
      setValue("endDate", undefined);
  }, [startDate, endDate]);

  const [scheduleUnit, setScheduleUnit] = useState("months");
  const [scheduleCount, setScheduleCount] = useState("1");

  const [dayOfWeek, setDayOfWeek] = useState("1");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [month, setMonth] = useState("0");

  const [dayOfMonthOption, setDayOfMonthOption] = useState("custom");
  const [hour, setHour] = useState("9");
  const [minute, setMinute] = useState("0");

  useEffect(() => {
    setDayOfMonthOption("custom");
  }, []);

  useEffect(() => {
    const start = startDate ? new Date(startDate) : startDate;

    if (start && !defaultValues?.schedule) {
      setDayOfWeek(start.getDay().toString());
      setDayOfMonth(start.getDate().toString());
      setMonth(start.getMonth().toString());
    }
  }, [startDate]);

  const scheduleTypes = useMemo(
    () => [
      { value: "scheduled", label: t("templates.scheduleType.scheduled") },
      { value: "onetime", label: t("templates.scheduleType.onetime") },
      {
        value: "startAndFinish",
        label: t("templates.scheduleType.startAndFinish"),
      },
    ],
    []
  );

  const scheduleUnits = useMemo(
    () => [
      { value: "days", label: t("templates.scheduleUnits.days") },
      { value: "weeks", label: t("templates.scheduleUnits.weeks") },
      { value: "months", label: t("templates.scheduleUnits.months") },
      { value: "years", label: t("templates.scheduleUnits.years") },
    ],
    []
  );

  const dayOfMonthOptions = useMemo(
    () => [
      { value: "first", label: t("templates.dayOfMonthOptions.first") },
      { value: "last", label: t("templates.dayOfMonthOptions.last") },
      { value: "custom", label: t("templates.dayOfMonthOptions.custom") },
    ],
    []
  );

  const dateFnsLocale = useDateFnsLocale();

  const monthsOptions = useMemo(
    () =>
      times(12, (value) => ({
        value: value.toString(),
        label: dateFnsLocale.localize.month(value),
      })),
    []
  );

  const handleChangeScheduleCount = useCallback(
    (e) => {
      setScheduleCount(e.target.value);
    },
    [setScheduleCount]
  );

  const handleChangeScheduleUnit = useCallback(
    ({ value }) => {
      setScheduleUnit(value);
      if (value === "years") {
        setScheduleCount("1");
      }
    },
    [setScheduleUnit, setScheduleCount]
  );

  const handleChangeDayOfMonthOption = useCallback(
    ({ value }) => {
      setDayOfMonthOption(value);
    },
    [setDayOfMonthOption]
  );

  const handleChangeDayOfMonth = useCallback(
    (e) => {
      setDayOfMonth(e.target.value);
    },
    [setMonth]
  );

  const handleChangeMonth = useCallback(
    ({ value }) => {
      setMonth(value);
    },
    [setMonth]
  );

  const handleChangeScheduleType = useCallback(
    ({ value }) => {
      if (value === "onetime") setValue("endDate", undefined);
      setScheduleType(value);
    },
    [setMonth]
  );

  const defaultStartDate = useMemo(
    () =>
      defaultValues?.startDate ? new Date(defaultValues.startDate) : new Date(),
    [defaultValues]
  );

  const scheduleItems = useSchedule({ schedule: defaultValues?.schedule });
  useEffect(() => {
    if (defaultValues?.schedule) {
      setScheduleUnit(scheduleItems.scheduleUnit);
      setScheduleCount(scheduleItems.scheduleCount);
      setDayOfWeek(scheduleItems.dayOfWeek);
      setDayOfMonth(scheduleItems.dayOfMonth);
      setMonth(scheduleItems.month);
      setHour(scheduleItems.hour);
      setMinute(scheduleItems.minute);
      setDayOfMonthOption(scheduleItems.dayOfMonthOption)
    }
  }, [defaultValues?.schedule]);

  const [scheduleType, setScheduleType] = useState(
    !defaultValues || defaultValues.schedule ? "scheduled" : (endDate ? "startAndFinish" : "onetime")
  );

  const schedule = useMemo(() => {
    if (scheduleType === "scheduled") {
      switch (scheduleUnit) {
        case "days":
          return `${minute} ${hour} */${scheduleCount} * *`;

        case "weeks":
          return `${minute} ${hour} * * ${dayOfWeek}%${scheduleCount}`;

        case "months":
          switch (dayOfMonthOption) {
            case "first":
              return `${minute} ${hour} 1 */${scheduleCount} *`;
            case "last":
              return `${minute} ${hour} L */${scheduleCount} *`;
            case "custom":
              return `${minute} ${hour} ${dayOfMonth} */${scheduleCount} *`;
            default:
              return null;
          }

        case "years":
          return `${minute} ${hour} ${dayOfMonth} ${Number(month) + 1} *`;
        case "onetime":
          return null;

        default:
          return null;
      }
    } else return null;
  }, [
    minute,
    hour,
    scheduleUnit,
    scheduleCount,
    dayOfWeek,
    dayOfMonth,
    dayOfMonthOption,
    month,
    scheduleType,
  ]);

  useEffect(() => setValue("schedule", schedule), [schedule]);

  return (
    <>
      <input type="hidden" name="schedule" ref={register()} />
      <div className="flex">
        <Select
          items={scheduleTypes}
          id="scheduleTypes"
          name="scheduleTypes"
          label="Report schedule type"
          srOnlyLabel
          defaultValue={find(scheduleTypes, { value: scheduleType })}
          renderItem={(i) => i.label}
          getValue={(i) => i.value}
          onChange={handleChangeScheduleType}
        />
      </div>

      <DatePicker
        name="startDate"
        label={
          scheduleType === "onetime"
            ? (t("mads.startDateOneTimeLabel") as string)
            : scheduleType === "startAndFinish"
            ? (t("mads.startDateStartAndFinishLabel") as string)
            : (t("mads.startDateScheduledLabel") as string)
        }
        defaultValue={defaultStartDate}
        RHF={{
          register,
          name: "startDate",
          unregister,
          setValue,
          registerOptions: {
            required: true,
          },
        }}
      />
      {startDate && (
        <>
          {scheduleType === "scheduled" && (
            <FormLabel label={t("templates.everyInterval")}>
              <div className="flex flex-row items-center space-x-2 text-sm">
                <Input
                  type="number"
                  min="1"
                  max="100"
                  label="Interval count"
                  srOnlyLabel
                  id="intervalCount"
                  value={scheduleCount}
                  disabled={scheduleUnit === "years"}
                  onChange={handleChangeScheduleCount}
                />
                <Select
                  items={scheduleUnits}
                  id="scheduleUnit"
                  label="Schedule unit"
                  srOnlyLabel
                  defaultValue={scheduleUnit}
                  renderItem={(i) => i.label}
                  getValue={(i) => i.value}
                  onChange={handleChangeScheduleUnit}
                />
              </div>
            </FormLabel>
          )}
          {scheduleType === "scheduled" && scheduleUnit === "weeks" && (
            <FormLabel
              label={t("templates.dayOfWeekField")}
              className="inline-block"
            >
              <div className="flex flex-row items-center space-x-1 text-sm">
                {["1", "2", "3", "4", "5", "6", "0"].map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`flex items-center justify-center w-8 h-8 text-xs rounded-full
                            ${
                              dayOfWeek === day
                                ? "bg-primary-500 text-light-100"
                                : "bg-light-100 text-light-900"
                            }
                            `}
                    onClick={() => setDayOfWeek(day)}
                  >
                    {dateFnsLocale.localize.day(day, { width: "narrow" })}
                  </button>
                ))}
              </div>
            </FormLabel>
          )}
          {scheduleType === "scheduled" && scheduleUnit === "months" && (
            <FormLabel label={t("templates.dayOfMonthField")} className="w-96">
              <Select
                items={dayOfMonthOptions}
                id="dayOfMonthOption"
                label="Day of the month"
                srOnlyLabel
                defaultValue={dayOfMonthOption}
                renderItem={(i) => i.label}
                getValue={(i) => i.value}
                onChange={handleChangeDayOfMonthOption}
              />
              {dayOfMonthOption === "custom" && (
                <div className="grid grid-cols-7 gap-3 pt-2 mt-4 text-sm w-96 auto-cols-min">
                  {times(31, (day) => (day + 1).toString()).map((day) => (
                    <button
                      key={day}
                      type="button"
                      className={`items-center justify-center w-8 h-8 text-xs rounded-full
                                ${
                                  dayOfMonth === day
                                    ? "bg-primary-500 text-light-100"
                                    : `${
                                        Number(day) <= 28
                                          ? "bg-light-100"
                                          : "bg-warning-100"
                                      } text-light-900`
                                }
                                `}
                      onClick={() => setDayOfMonth(day)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              )}
            </FormLabel>
          )}
          {scheduleType === "scheduled" && scheduleUnit === "years" && (
            <FormLabel
              label={t("templates.dayOfYearField")}
              className="inline-block"
            >
              <div className="flex flex-row items-center space-x-2 text-sm">
                <Input
                  type="number"
                  min="1"
                  id="monthDayNumber"
                  label="Day in the month"
                  srOnlyLabel
                  max={DAYS_IN_MONTH[Number(month)]}
                  value={dayOfMonth}
                  onChange={handleChangeDayOfMonth}
                />
                <Select
                  items={monthsOptions}
                  label="Month"
                  srOnlyLabel
                  id="month"
                  defaultValue={month}
                  renderItem={(i) => i.label}
                  getValue={(i) => i.value}
                  onChange={handleChangeMonth}
                />
              </div>
            </FormLabel>
          )}
          {scheduleType !== "onetime" && (
            <FormLabel
              label={
                scheduleType === "startAndFinish"
                  ? t("mads.endReportLabel")
                  : t("mads.endDateLabel")
              }
            >
              <div className="flex">
                <DatePicker
                  name="endDate"
                  minDate={startDate ? new Date(startDate) : undefined}
                  RHF={{
                    register,
                    name: "endDate",
                    unregister,
                    setValue,
                  }}
                  defaultValue={
                    defaultValues?.endDate
                      ? new Date(defaultValues?.endDate)
                      : undefined
                  }
                />
              </div>
            </FormLabel>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(MadSchedulingForm);
